import { useEffect, useState } from 'react';
import { applyAudio, snapshotAudio } from '../audio/settings';
import { playHurt, playSkill, tocarUo } from '../audio/mixer';
import { aplicarAvisos, snapshotAvisos } from '../avisos/preferencia';
import { aplicarEstiloBalao, estiloBalao, type EstiloBalao } from '../world/balao';
import {
  ACOES,
  ACOES_NOVAS,
  acaoFixa,
  aplicarAtalho,
  conflitoCom,
  emConflito,
  GRUPOS,
  iniciarCaptura,
  mostrarNovas,
  restaurarAtalhos,
  rotuloTecla,
  teclaDe,
  type Acao
} from '../input/atalhos';
import { useAtalhos, useFechar } from '../input/useAtalhos';
import { IDIOMAS, NOME_DO_IDIOMA, idiomaAtual, numero, t, trocarIdioma } from '../i18n/idioma';
import { useIdioma } from '../i18n/useIdioma';
import { Cartao, Chave } from './hud/Cartao';
import { DiscordCartao, type RetornoDiscord } from './DiscordCartao';
import { IconeHud } from './hud/icones';
import { restaurarPosicoes } from './hud/posicoes';
import { gravarBotaoAndar, lerBotaoAndar } from '../input/botaoAndar';
import { gravarMarcaClique, lerMarcaClique, type MarcaClique } from '../world/marcaClique';
import { MarcaCliqueOpcoes } from './hud/MarcaCliqueOpcoes';
import { aplicarFundoBarra, BARRA_ESCALA_MAX, BARRA_ESCALA_MIN, BARRA_SLOTS_MAX, BARRA_SLOTS_MIN, gravarBarra, gravarConfirmarInocente, lerBarra, lerConfirmarInocente, lerFundoBarra } from './hud/preferencias';

interface Props {
  onClose(): void;
  onTrocarPersonagem(): void;
  nome: string;
  lugar: string;
  shard: string;
  /** A seção que abre primeiro (a volta do Discord abre em Conta). */
  abaInicial?: Secao;
  /** O resultado da volta do Discord, para a seção Conta mostrar. */
  retornoDiscord?: RetornoDiscord | null;
}

export type Secao = 'jogo' | 'jogabilidade' | 'interface' | 'audio' | 'atalhos' | 'conta' | 'recebimentos';

const ESTILOS_BALAO: readonly EstiloBalao[] = ['balao', 'classico', 'nada'];

const SECOES: ReadonlyArray<{ id: Secao; nome: string; desc: string; icone: string }> = [
  { id: 'jogo', nome: 'hud.cfg.jogo', desc: 'hud.cfg.jogoDesc', icone: 'config' },
  { id: 'jogabilidade', nome: 'hud.cfg.jogabilidade', desc: 'hud.cfg.jogabilidadeDesc', icone: 'treinar' },
  { id: 'interface', nome: 'hud.cfg.interface', desc: 'hud.cfg.interfaceDesc', icone: 'mais' },
  { id: 'audio', nome: 'opcoes.audio', desc: 'hud.cfg.audioDesc', icone: 'som' },
  { id: 'atalhos', nome: 'atalho.titulo', desc: 'hud.cfg.atalhosDesc', icone: 'teclado' },
  { id: 'conta', nome: 'hud.cfg.conta', desc: 'hud.cfg.contaDesc', icone: 'chat' },
  { id: 'recebimentos', nome: 'hud.cfg.recebimentos', desc: 'hud.cfg.recebimentosDesc', icone: 'banco' }
];

/** Os volumes por tipo (docs/proposta-sons.md §07) e o som de exemplo de cada um. */
const CANAIS_DE_SOM = [
  { id: 'combate', ouvir: 0x23B },
  { id: 'criaturas', ouvir: 0x45A },
  { id: 'passos', ouvir: 0x12B },
  { id: 'ambiente', ouvir: 0x17 }
] as const;

export function SettingsModal({ onClose, onTrocarPersonagem, nome, lugar, shard, abaInicial, retornoDiscord }: Props) {
  useIdioma();
  useAtalhos();
  useFechar(onClose, { modal: true, deixaPassar: ['config'] });
  const idioma = idiomaAtual();
  const snap = snapshotAudio();
  const [aba, setAba] = useState<Secao>(abaInicial ?? 'jogo');
  const [fundoBarra, setFundoBarra] = useState(lerFundoBarra);
  const [barra, setBarra] = useState(lerBarra);
  const [confirmarInocente, setConfirmarInocente] = useState(lerConfirmarInocente);
  const [andarEsquerdo, setAndarEsquerdo] = useState(() => lerBotaoAndar() === 'esquerdo');
  const [marca, setMarca] = useState<MarcaClique>(lerMarcaClique);
  const [muted, setMuted] = useState(() => snap.muted);
  const [master, setMaster] = useState(() => snap.master);
  const [skills, setSkills] = useState(() => snap.skills);
  const [canais, setCanais] = useState(() => ({ combate: snap.combate, criaturas: snap.criaturas, passos: snap.passos, ambiente: snap.ambiente }));
  const [avisosNaTela, setAvisosNaTela] = useState(() => snapshotAvisos().mostrarNaTela);
  const [balao, setBalao] = useState<EstiloBalao>(() => estiloBalao());
  const [capturando, setCapturando] = useState<Acao | null>(null);
  const [recusa, setRecusa] = useState<'modificador' | 'navegador' | null>(null);
  const [busca, setBusca] = useState('');
  const [restaurou, setRestaurou] = useState(false);

  useEffect(() => {
    if (!capturando) return;
    return iniciarCaptura(capturando, r => {
      if (r.ok) {
        aplicarAtalho(capturando, r.tecla);
        setCapturando(null);
        setRecusa(null);
        return;
      }
      if (r.motivo === 'cancelou') {
        setCapturando(null);
        setRecusa(null);
        return;
      }
      setRecusa(r.motivo);
    });
  }, [capturando]);

  const masterPct = Math.round(master * 100);
  const skillsPct = Math.round(skills * 100);
  const q = busca.trim().toLowerCase();
  const nConflitos = ACOES.filter(emConflito).length;

  let painel;
  if (aba === 'jogo') {
    painel = (
      <>
        {/* Trocar de personagem fica no topo de Jogo, em destaque: é o que mais se procura aqui. */}
        <Cartao titulo={t('personagem.trocarPersonagem')} desc={t('hud.cfg.personagemAjuda')} classe="p-card--destaque">
          <div className="p-opt">
            <span><b>{nome}</b><small>{`${lugar} · ${shard}`}</small></span>
            <button type="button" className="btn btn--primary" onClick={onTrocarPersonagem}>{t('personagem.trocarPersonagem')}</button>
          </div>
        </Cartao>
        <Cartao titulo={t('opcoes.idioma')} desc={t('hud.cfg.idiomaDesc')}>
          <div className="p-seg" role="radiogroup" aria-label={t('opcoes.idioma')}>
            {IDIOMAS.map(id => (
              <button key={id} type="button" role="radio" aria-checked={id === idioma} onClick={() => { void trocarIdioma(id); }}>
                {NOME_DO_IDIOMA[id]}
              </button>
            ))}
          </div>
        </Cartao>
        <Cartao titulo={t('balao.cfg.titulo')} desc={t('balao.cfg.desc')}>
          <div className="p-seg" role="radiogroup" aria-label={t('balao.cfg.titulo')}>
            {ESTILOS_BALAO.map(id => (
              <button key={id} type="button" role="radio" aria-checked={id === balao}
                onClick={() => { setBalao(id); aplicarEstiloBalao(id); }}>
                {t(`balao.cfg.${id}`)}
              </button>
            ))}
          </div>
        </Cartao>
        <Cartao titulo={t('aviso.secao')}>
          <div className="p-opt">
            <span><b>{t('aviso.mostrarNaTela')}</b><small>{t('aviso.mostrarNaTelaAjuda')}</small></span>
            <Chave on={avisosNaTela} onClick={() => {
              const next = !avisosNaTela;
              setAvisosNaTela(next);
              aplicarAvisos({ mostrarNaTela: next });
            }} />
          </div>
        </Cartao>
      </>
    );
  } else if (aba === 'jogabilidade') {
    painel = (
      <>
        <Cartao titulo={t('hud.cfg.movimento')}>
          <div className="p-opt">
            <span><b>{t('hud.cfg.andarEsquerdo')}</b><small>{t('hud.cfg.andarEsquerdoAjuda')}</small></span>
            <Chave on={andarEsquerdo} onClick={() => {
              const next = !andarEsquerdo;
              setAndarEsquerdo(next);
              gravarBotaoAndar(next ? 'esquerdo' : 'direito');
            }} />
          </div>
        </Cartao>
        <Cartao titulo={t('hud.cfg.combate')}>
          <div className="p-opt">
            <span><b>{t('hud.cfg.confirmarInocente')}</b><small>{t('hud.cfg.confirmarInocenteAjuda')}</small></span>
            <Chave on={confirmarInocente} onClick={() => {
              const next = !confirmarInocente;
              setConfirmarInocente(next);
              gravarConfirmarInocente(next);
            }} />
          </div>
        </Cartao>
      </>
    );
  } else if (aba === 'interface') {
    // techspec-hud-v3 §19: a transparência do fundo da barra de skills e as posições das janelas e botões.
    painel = (
      <>
        <Cartao titulo={t('hud.cfg.barra')} desc={t('hud.cfg.barraDesc')}>
          <div className="p-vol">
            <span className="p-vol__n"><b>{t('hud.cfg.barraTamanho')}</b><small>{t('hud.cfg.barraTamanhoAjuda')}</small></span>
            <input
              type="range"
              min={BARRA_ESCALA_MIN}
              max={BARRA_ESCALA_MAX}
              step={5}
              value={barra.escala}
              aria-label={t('hud.cfg.barraTamanho')}
              aria-valuetext={t('opcoes.porCento', { n: barra.escala })}
              onChange={e => setBarra(gravarBarra({ escala: Number(e.target.value) }))}
            />
            <span className="p-vol__pct">{numero(barra.escala)}%</span>
          </div>
          <div className="p-vol">
            <span className="p-vol__n"><b>{t('hud.cfg.barraSlots')}</b><small>{t('hud.cfg.barraSlotsAjuda')}</small></span>
            <input
              type="range"
              min={BARRA_SLOTS_MIN}
              max={BARRA_SLOTS_MAX}
              step={1}
              value={barra.slots}
              aria-label={t('hud.cfg.barraSlots')}
              aria-valuetext={numero(barra.slots)}
              onChange={e => setBarra(gravarBarra({ slots: Number(e.target.value) }))}
            />
            <span className="p-vol__pct">{numero(barra.slots)}</span>
          </div>
          <div className="p-opt">
            <span><b>{t('hud.cfg.barraLinhas')}</b><small>{t('hud.cfg.barraLinhasAjuda')}</small></span>
            <Chave on={barra.duasLinhas} onClick={() => setBarra(gravarBarra({ duasLinhas: !barra.duasLinhas }))} />
          </div>
          <div className="p-vol">
            <span className="p-vol__n"><b>{t('hud.cfg.barraFundo')}</b><small>{t('hud.cfg.barraFundoAjuda')}</small></span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={fundoBarra}
              aria-label={t('hud.cfg.barraFundo')}
              aria-valuetext={t('opcoes.porCento', { n: fundoBarra })}
              onChange={e => { const n = Number(e.target.value); setFundoBarra(n); aplicarFundoBarra(n, true); }}
            />
            <span className="p-vol__pct">{numero(fundoBarra)}%</span>
          </div>
        </Cartao>
        <Cartao titulo={t('hud.cfg.marcaClique')} desc={t('hud.cfg.marcaCliqueDesc')}>
          <MarcaCliqueOpcoes valor={marca} onEscolher={id => { setMarca(id); gravarMarcaClique(id); }} />
        </Cartao>
        <Cartao titulo={t('hud.cfg.posicoes')} desc={t('hud.cfg.posicoesAjuda')}>
          <div className="p-opt">
            <button type="button" className="btn" onClick={() => { restaurarPosicoes(); setRestaurou(true); }}>
              {t('hud.cfg.restaurarPosicoes')}
            </button>
            {restaurou && <small role="status">{t('hud.cfg.posicoesRestauradas')}</small>}
          </div>
        </Cartao>
      </>
    );
  } else if (aba === 'audio') {
    painel = (
      <Cartao titulo={t('opcoes.audio')}>
        <div className="p-opt">
          <span><b>{t('opcoes.mudo')}</b><small>{t('hud.cfg.mudoAjuda')}</small></span>
          <Chave on={muted} onClick={() => {
            const next = !muted;
            setMuted(next);
            applyAudio({ muted: next });
          }} />
        </div>
        <Volume nome={t('opcoes.master')} ajuda={t('hud.cfg.geralAjuda')} pct={masterPct} mudo={muted}
          onChange={n => { setMaster(n / 100); applyAudio({ master: n / 100 }); }}
          ouvir={() => playHurt()} />
        <Volume nome={t('opcoes.magias')} ajuda={t('hud.cfg.magiasAjuda')} pct={skillsPct} mudo={muted}
          onChange={n => { setSkills(n / 100); applyAudio({ skills: n / 100 }); }}
          ouvir={() => playSkill('magic-arrow')} />
        {CANAIS_DE_SOM.map(c => (
          <Volume key={c.id} nome={t(`opcoes.som.${c.id}`)} ajuda={t(`opcoes.som.${c.id}Ajuda`)} pct={Math.round(canais[c.id] * 100)} mudo={muted}
            onChange={n => { setCanais(cur => ({ ...cur, [c.id]: n / 100 })); applyAudio({ [c.id]: n / 100 }); }}
            ouvir={() => tocarUo(c.ouvir, c.id)} />
        ))}
      </Cartao>
    );
  } else if (aba === 'atalhos') {
    painel = (
      <>
        <div className="p-binds-topo">
          <label className="field p-busca">
            <input type="search" placeholder={t('hud.cfg.procurar')} aria-label={t('hud.cfg.procurar')} value={busca} onChange={e => setBusca(e.target.value)} />
          </label>
          <button type="button" className="btn btn--tiny btn--ghost" onClick={() => { restaurarAtalhos(); setCapturando(null); setRecusa(null); }}>
            {t('atalho.restaurar')}
          </button>
        </div>
        <p className="p-ajuda">
          {capturando
            ? <>{t('hud.cfg.aperte')} <b>{t(`atalho.acao.${capturando}`)}</b>. <kbd>Esc</kbd> {t('hud.cfg.cancela')}</>
            : t('hud.cfg.ajuda')}
          {recusa && <span className="p-alerta">{t(`atalho.recusa.${recusa}`)}</span>}
          {nConflitos > 0 && <span className="p-alerta">{t('hud.cfg.conflitos', { n: nConflitos })}</span>}
        </p>
        <div className="p-binds">
          {GRUPOS.map(g => {
            const linhas = g.acoes.filter(a => !q || t(`atalho.acao.${a}`).toLowerCase().includes(q));
            if (linhas.length === 0) return null;
            return (
              <section className="p-card p-card--bind" key={g.chave}>
                <h3>{t(g.chave)}</h3>
                {linhas.map(a => (
                  <LinhaAtalho
                    key={a}
                    acao={a}
                    capturando={capturando === a}
                    onCapturar={() => {
                      if (acaoFixa(a)) return;
                      setRecusa(null);
                      setCapturando(atual => atual === a ? null : a);
                    }}
                  />
                ))}
              </section>
            );
          })}
        </div>
      </>
    );
  } else if (aba === 'recebimentos') {
    // techspec-pix-leilao (T1): por enquanto só anuncia o Pix entre jogadores. O botão não faz nada.
    painel = <RecebimentosEmBreve />;
  } else {
    // techspec-discord-cargos §09: vincular o Discord e ver os cargos de VIP e Fundador.
    painel = <DiscordCartao retorno={retornoDiscord ?? null} />;
  }

  return (
    // Clicar fora não fecha: só o × e o Fechar (e o Esc, como toda janela do jogo).
    <div className="modal" role="presentation">
      <div role="dialog" aria-modal="true" aria-label={t('opcoes.titulo')} className="p-cfg">
        <section className="gump">
          <header className="gump__head">
            <h2 className="gump__title">{t('opcoes.titulo')}</h2>
            <button type="button" className="btn btn--tiny btn--ghost win__close" onClick={onClose} aria-label={t('comum.fechar')}>×</button>
          </header>
          <div className="gump__body p-cfg__corpo">
            <nav className="p-cfg__nav" role="tablist" aria-label={t('hud.cfg.secoes')}>
              {SECOES.map(s => (
                <button type="button" role="tab" aria-selected={aba === s.id} onClick={() => { setAba(s.id); setCapturando(null); }} key={s.id}>
                  <IconeHud nome={s.icone} /><span><b>{t(s.nome)}</b><small>{t(s.desc)}</small></span>
                </button>
              ))}
            </nav>
            <div className="p-cfg__painel" role="tabpanel">{painel}</div>
          </div>
          <footer className="p-cfg__pe"><button type="button" className="btn btn--tiny win__close" onClick={onClose}>{t('comum.fechar')}</button></footer>
        </section>
      </div>
    </div>
  );
}

function Volume({
  nome, ajuda, pct, mudo, onChange, ouvir
}: {
  nome: string;
  ajuda: string;
  pct: number;
  mudo: boolean;
  onChange(pct: number): void;
  ouvir(): void;
}) {
  return (
    <div className={`p-vol${mudo ? ' is-off' : ''}`}>
      <span className="p-vol__n"><b>{nome}</b><small>{ajuda}</small></span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={pct}
        disabled={mudo}
        aria-label={nome}
        aria-valuetext={t('opcoes.porCento', { n: pct })}
        onChange={e => onChange(Number(e.target.value))}
      />
      <span className="p-vol__pct">{numero(pct)}%</span>
      <button type="button" className="btn btn--tiny btn--ghost" disabled={mudo} aria-label={t('hud.cfg.ouvirAria', { nome })} onClick={ouvir}>
        {t('hud.cfg.ouvir')}
      </button>
    </div>
  );
}

function LinhaAtalho({ acao, capturando, onCapturar }: { acao: Acao; capturando: boolean; onCapturar(): void }) {
  const fixa = acaoFixa(acao);
  const conflito = conflitoCom(acao);
  return (
    <div className={`p-bind${conflito ? ' is-conflict' : ''}${capturando ? ' is-capturing' : ''}`}>
      <span className="p-bind__n">
        {t(`atalho.acao.${acao}`)}
        {mostrarNovas && ACOES_NOVAS.has(acao) && <> <em className="p-nova">{t('hud.nova')}</em></>}
        {conflito && <small>{t('atalho.conflito', { acao: t(`atalho.acao.${conflito}`) })}</small>}
      </span>
      <button
        type="button"
        className="settings-binds__tecla p-tecla"
        disabled={fixa}
        title={fixa ? t('atalho.fixa') : undefined}
        onClick={onCapturar}
      >
        {capturando ? t('hud.cfg.aperteCurto') : rotuloTecla(teclaDe(acao))}{fixa ? ' 🔒' : ''}
      </button>
    </div>
  );
}

/** Recebimentos por Pix (techspec-pix-leilao, T1), ainda em breve: explica o que vem e o que vai ser preciso. */
function RecebimentosEmBreve() {
  return (
    <Cartao classe="p-receb" titulo={<>{t('receb.titulo')} <em className="p-nova">{t('comum.emBreve')}</em></>} desc={t('receb.desc')}>
      <p className="p-receb__req">{t('receb.req')}</p>
      <ul className="p-receb__lista">
        <li>{t('receb.req2fa')}</li>
        <li>{t('receb.req18')}</li>
        <li>{t('receb.reqMp')}</li>
      </ul>
      <button type="button" className="btn btn--primary" disabled>{t('receb.vincular')}</button>
      <p className="p-receb__breve">{t('receb.breve')}</p>
    </Cartao>
  );
}
